import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';
import type { ConnectionStatus } from './types';

// Origins Meta serves its OAuth consent screen from.
const FACEBOOK_OAUTH_HOSTS = ['www.facebook.com', 'facebook.com', 'm.facebook.com'];

/**
 * Confirm a URL really points at Facebook's consent screen before we send an
 * admin there.
 *
 * Returns the reassembled URL, or null if it is anything else. Navigating to a
 * URL that came back over the wire is how an open redirect starts: the value
 * looks trustworthy because our own API produced it, but a phishing page that
 * imitates the Facebook login is indistinguishable to whoever is clicking.
 */
function assertFacebookOAuthUrl(rawUrl: string | undefined): string | null {
  if (!rawUrl) return null;

  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return null;
  }

  if (parsed.protocol !== 'https:') return null;
  if (!FACEBOOK_OAUTH_HOSTS.includes(parsed.hostname)) return null;

  return `https://${parsed.hostname}${parsed.pathname}${parsed.search}`;
}

const SETUP_STEPS = [
  {
    title: 'Convert your Instagram account to a Business account',
    detail: 'In the Instagram app: Settings → Account type and tools → Switch to professional account → Business.',
    link: 'https://help.instagram.com/502981923235522',
  },
  {
    title: 'Create a Facebook Page and link Instagram to it',
    detail: 'Instagram Business accounts must be connected to a Facebook Page to use the publishing API.',
    link: 'https://www.facebook.com/pages/create',
  },
  {
    title: 'Set up Meta Business Manager',
    detail: 'Add both the Facebook Page and the Instagram account as assets, then create an Ad Account for paid campaigns.',
    link: 'https://business.facebook.com',
  },
  {
    title: 'Create a Facebook App',
    detail: 'At developers.facebook.com create a Business-type app, add the Instagram Graph API and Marketing API products, then copy the App ID and App Secret into the server environment.',
    link: 'https://developers.facebook.com/apps',
  },
  {
    title: 'Submit for App Review',
    detail: 'Request instagram_basic, instagram_content_publish, pages_read_engagement, ads_management, and ads_read. Publishing to a live account requires approval.',
    link: 'https://developers.facebook.com/docs/app-review',
  },
];

const STATUS_BADGES: Record<string, { label: string; className: string }> = {
  connected: { label: 'CONNECTED', className: 'bg-scarf-green/20 text-scarf-green' },
  disconnected: { label: 'NOT CONNECTED', className: 'bg-gray-100 text-gray-600' },
  token_expired: { label: 'TOKEN EXPIRED', className: 'bg-yellow-100 text-yellow-800' },
  error: { label: 'ERROR', className: 'bg-brand-red/20 text-brand-red' },
};

const SettingsComponent: React.FC = () => {
  const [connection, setConnection] = useState<ConnectionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isWorking, setIsWorking] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const notify = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 6000);
  };

  const loadConnection = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await apiService.getInstagramConnectionStatus() as unknown as {
        connection: ConnectionStatus;
      };
      setConnection(res.connection);
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Failed to load connection status');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConnection();

    // The OAuth callback redirects back here with a result in the query string.
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected') === 'true') {
      notify('success', 'Instagram account connected.');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('error')) {
      notify('error', decodeURIComponent(params.get('error')!));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [loadConnection]);

  const handleConnect = async () => {
    setIsWorking(true);
    try {
      const res = await apiService.getInstagramOAuthUrl() as unknown as { oauthUrl: string };

      // Checked before navigating even though the URL is our own API's answer.
      // This is the one place the admin session is handed to another origin,
      // so a compromised or misconfigured backend must not be able to steer an
      // admin somewhere that impersonates the Facebook consent screen.
      const target = assertFacebookOAuthUrl(res.oauthUrl);
      if (!target) {
        notify('error', 'The authorisation link was not a valid Facebook URL. Check META_OAUTH_REDIRECT_URI.');
        setIsWorking(false);
        return;
      }

      window.location.href = target;
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Could not start the connection flow');
      setIsWorking(false);
    }
  };

  const handleRefresh = async () => {
    setIsWorking(true);
    try {
      await apiService.refreshInstagramToken();
      notify('success', 'Access token refreshed.');
      loadConnection();
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Refresh failed');
    } finally {
      setIsWorking(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect the Instagram account? Scheduled posts will stop publishing.')) {
      return;
    }

    setIsWorking(true);
    try {
      await apiService.disconnectInstagram();
      notify('success', 'Account disconnected.');
      loadConnection();
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Disconnect failed');
    } finally {
      setIsWorking(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue" />
      </div>
    );
  }

  const badge = STATUS_BADGES[connection?.connectionStatus || 'disconnected'];
  const isConnected = connection?.connectionStatus === 'connected';

  return (
    <div className="space-y-6">
      {message && (
        <div className={`rounded-xl px-6 py-4 font-body ${
          message.type === 'success'
            ? 'bg-scarf-green/10 border border-scarf-green/30 text-scarf-green'
            : 'bg-brand-red/10 border border-brand-red/30 text-brand-red'
        }`}>
          {message.text}
        </div>
      )}

      {/* Connection */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-title font-bold text-deep-navy">Meta Connection</h3>
          <span className={`px-4 py-2 rounded-full text-xs font-bold ${badge.className}`}>
            {badge.label}
          </span>
        </div>

        {isConnected ? (
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-frost-white rounded-xl p-4">
                <p className="text-sm text-gray-600 font-body">Instagram Account</p>
                <p className="text-lg font-bold text-deep-navy">@{connection.instagramUsername}</p>
              </div>
              <div className="bg-frost-white rounded-xl p-4">
                <p className="text-sm text-gray-600 font-body">Facebook Page</p>
                <p className="text-lg font-bold text-deep-navy">{connection.facebookPageName}</p>
              </div>
              <div className="bg-frost-white rounded-xl p-4">
                <p className="text-sm text-gray-600 font-body">Ad Account</p>
                <p className="text-lg font-bold text-deep-navy">
                  {connection.adAccountId || 'Not linked'}
                </p>
              </div>
              <div className="bg-frost-white rounded-xl p-4">
                <p className="text-sm text-gray-600 font-body">Token Expires</p>
                <p className="text-lg font-bold text-deep-navy">
                  {connection.tokenExpiresAt
                    ? new Date(connection.tokenExpiresAt).toLocaleDateString()
                    : 'Does not expire'}
                </p>
              </div>
            </div>

            {connection.permissions && connection.permissions.length > 0 && (
              <div>
                <p className="text-sm font-bold text-deep-navy mb-2 font-body">Granted Permissions</p>
                <div className="flex flex-wrap gap-2">
                  {connection.permissions.map((p) => (
                    <span key={p} className="px-3 py-1 bg-ice-pale rounded-full text-xs font-body text-deep-navy">
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleRefresh}
                disabled={isWorking}
                className="bg-brand-blue hover:bg-brand-blue-dark text-white font-bold px-6 py-3 rounded-xl transition-colors disabled:opacity-50"
              >
                Refresh Token
              </button>
              <button
                onClick={handleDisconnect}
                disabled={isWorking}
                className="bg-brand-red hover:bg-brand-red-light text-white font-bold px-6 py-3 rounded-xl transition-colors disabled:opacity-50"
              >
                Disconnect
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {connection?.lastError && (
              <p className="text-brand-red font-body text-sm">{connection.lastError}</p>
            )}

            {!connection?.configured ? (
              <div className="bg-yellow-50 border border-yellow-300 rounded-xl px-6 py-4">
                <p className="font-body text-yellow-800">
                  The server is missing <code className="font-mono text-sm">META_APP_ID</code> and{' '}
                  <code className="font-mono text-sm">META_APP_SECRET</code>. Complete the setup steps
                  below, then add them to the backend environment before connecting.
                </p>
              </div>
            ) : (
              <p className="text-gray-600 font-body">
                Connect your Instagram Business account to publish posts and run campaigns.
              </p>
            )}

            <button
              onClick={handleConnect}
              disabled={isWorking || !connection?.configured}
              className="bg-brand-blue hover:bg-brand-blue-dark text-white font-bold px-6 py-3 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isWorking ? 'Redirecting…' : 'Connect Instagram Account'}
            </button>
          </div>
        )}
      </div>

      {/* AI status */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-xl font-title font-bold text-deep-navy">AI Content Generation</h3>
            <p className="text-gray-600 font-body text-sm mt-1">
              Powers caption writing, hashtag suggestions, and campaign copy.
            </p>
          </div>
          <span className={`px-4 py-2 rounded-full text-xs font-bold ${
            connection?.contentGenerationEnabled
              ? 'bg-scarf-green/20 text-scarf-green'
              : 'bg-gray-100 text-gray-600'
          }`}>
            {connection?.contentGenerationEnabled ? 'ENABLED' : 'NOT CONFIGURED'}
          </span>
        </div>

        {!connection?.contentGenerationEnabled && (
          <p className="text-gray-600 font-body text-sm mt-4">
            Set <code className="font-mono">GEMINI_API_KEY</code> in the backend environment to enable
            AI generation. Manual post creation works without it.
          </p>
        )}
      </div>

      {/* Setup guide — one-time onboarding, so it drops away once connected. */}
      {!isConnected && (
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-2">Setup Guide</h3>
        <p className="text-gray-600 font-body text-sm mb-6">
          Complete these once. Steps 1 through 3 happen in Meta&apos;s tools; step 4 produces the
          credentials the server needs.
        </p>

        <ol className="space-y-4">
          {SETUP_STEPS.map((step, index) => (
            <li key={step.title} className="bg-frost-white rounded-xl p-4 flex gap-4">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-brand-blue text-white font-bold flex items-center justify-center text-sm">
                {index + 1}
              </span>
              <div className="flex-1">
                <p className="font-bold text-deep-navy font-body">{step.title}</p>
                <p className="text-sm text-gray-600 font-body mt-1">{step.detail}</p>
                <a
                  href={step.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-brand-blue hover:text-brand-blue-dark font-bold mt-2 inline-block"
                >
                  Open →
                </a>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-6 bg-deep-navy rounded-xl p-4">
          <p className="text-white font-bold font-body text-sm mb-2">Backend environment variables</p>
          <pre className="text-ice-teal-light text-xs font-mono overflow-x-auto">
{`META_APP_ID=your-facebook-app-id
META_APP_SECRET=your-facebook-app-secret
META_OAUTH_REDIRECT_URI=https://server.waddleandsip.com/api/instagram/connection/callback
ADMIN_DASHBOARD_URL=https://admin.waddleandsip.com
INSTAGRAM_MONTHLY_BUDGET_CAP=100`}
          </pre>
        </div>
      </div>
      )}
    </div>
  );
};

export default SettingsComponent;
